import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCourseChat, type CourseChatEntry } from '../../hooks/useCourseChat';
import { coursePersonaLabel } from '../../lib/chat-persona';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { ChatConversationView } from '../chat/ChatConversationView';
import styles from './CourseChat.module.css';

/** Browser URL validation. The API performs authoritative target/version/access checks. */
export function courseEntryFromParams(params: URLSearchParams): CourseChatEntry | null {
  const values = Object.fromEntries(params);
  const ids = (keys: string[]): boolean => keys.every((key) => typeof values[key] === 'string' && values[key]!.length > 0 && values[key]!.length <= 100 && !/[\u0000-\u001f\u007f]/u.test(values[key]!));
  const exactly = (keys: string[]): boolean => [...params.keys()].length === keys.length && keys.every((key) => params.getAll(key).length === 1);
  if (exactly(['thread']) && ids(['thread'])) return { kind: 'thread', threadId: values.thread! };
  if ((values.kind === 'exercise' || values.kind === 'quiz') && exactly(['kind', 'lessonId', 'targetId']) && ids(['lessonId', 'targetId'])) {
    return { kind: 'target', target: { kind: values.kind, lessonId: values.lessonId!, targetId: values.targetId! } };
  }
  if (values.kind === 'exam_item' && exactly(['kind', 'examId', 'targetId', 'attemptId']) && ids(['examId', 'targetId', 'attemptId']) && /^[1-9]\d{0,9}$/.test(values.targetId!)) {
    return { kind: 'target', target: { kind: 'exam_item', examId: values.examId!, targetId: values.targetId!, attemptId: values.attemptId! } };
  }
  return null;
}

export function CourseChat(): JSX.Element {
  const location = useLocation();
  const [params] = useSearchParams();
  const { t } = useTranslation('course');
  const entry = courseEntryFromParams(params);
  if (!entry) return <MeSubpageShell title={t('pageTitle')}><p role="alert">{t('chat.gone')}</p><Link to="/course">{t('lesson.toCourse')}</Link></MeSubpageShell>;
  return <CourseConversation key={location.key} entry={entry} />;
}
function CourseConversation({ entry }: { entry: CourseChatEntry }): JSX.Element {
  const { t } = useTranslation(['course', 'home']);
  const chat = useCourseChat(entry);
  const quote = chat.preview;
  const disabled = !quote?.enabled || (entry.kind === 'target' && !chat.conversationId) || chat.error === 'chat_disabled' || chat.error === 'course_locked';
  const notice = (
    <aside className={styles.notice} aria-live="polite">
      {quote && !quote.enabled && <p role="status">{t('chat.disabled')}</p>}
      {quote?.targetMissing && <p role="status">{t('chat.missing')}</p>}
      {chat.messages.some((m) => m.billing === 'failed') && <p role="status">{t('chat.unpaid')}</p>}
      <Link to="/course">{t('lesson.toCourse')}</Link>
    </aside>
  );
  return <ChatConversationView chat={chat} target={{ kind: 'thread', threadId: chat.conversationId ?? (entry.kind === 'thread' ? entry.threadId : '') }} course={{ title: coursePersonaLabel(quote?.persona, t), disabled, loadCode: chat.loadCode, notice, quickReplies: chat.quickReplies, draftStorageKey: chat.draftStorageKey, priceStars: quote?.priceStars }} />;
}
